import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { ConsultationForm } from "@/components/ConsultationForm";
import { TRUST_POINTS, TRUST_ICON, CONTACT_EMAIL } from "@/data/content";
import { Check, Mail } from "lucide-react";

const Label = ({ children, className = "" }) => (
    <span className={`font-mono text-[11px] uppercase tracking-[0.24em] ${className}`}>
        {children}
    </span>
);

export default function Contact() {
    return (
        <div data-testid="contact-page" className="pt-[76px]">
            <Seo
                title="Contact DentAssists — Free Dental Billing Consultation in Texas"
                description="Request a free consultation with DentAssists. Serving dental practices in Texas and across the U.S. Tell us about your practice and a specialist will show you what we can take off your plate. Email Info@dentassists.com."
                path="/contact"
            />
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-20 grid lg:grid-cols-[0.85fr_1.15fr] gap-14">
                <Reveal>
                    <Label className="text-terracotta">Contact</Label>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tight text-ink mt-6 leading-[1]">
                        Ready to stop chasing claims?
                    </h1>
                    <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-md">
                        Tell us about your practice and we'll show you what we can take off your
                        plate. A U.S.-based specialist will follow up shortly.
                    </p>
                    <p className="text-base text-inksoft leading-relaxed mt-4 max-w-md">
                        The consultation is free and there's no obligation. Share your practice size,
                        the practice management software you use, and where billing hurts most —
                        aging claims, denials, verification backlogs, or credentialing delays. We'll
                        review your setup and walk you through exactly how DentAssists would handle
                        insurance verification, claims submission, payment posting, AR follow-up, and
                        reporting inside the system you already have. Prefer email? Reach us directly
                        at Info@dentassists.com and we'll respond within one business day.
                    </p>

                    <div className="mt-10 space-y-4">
                        {TRUST_POINTS.map((t) => (
                            <div key={t} className="flex items-start gap-3 text-sm text-inksoft">
                                <Check size={16} className="text-terracotta shrink-0 mt-0.5" />
                                {t}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 inline-flex items-center gap-3 bg-sage text-cream px-6 py-4 rounded-sm">
                        <TRUST_ICON size={22} className="text-terracotta" />
                        <span className="text-sm">HIPAA-compliant · Signed BAA on every engagement</span>
                    </div>

                    <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        data-testid="contact-email-link"
                        className="mt-6 flex items-center gap-3 text-ink hover:text-terracotta transition-colors duration-200 group"
                    >
                        <span className="w-11 h-11 rounded-sm border border-linec flex items-center justify-center group-hover:border-terracotta transition-colors">
                            <Mail size={18} className="text-terracotta" />
                        </span>
                        <span>
                            <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-inksoft">
                                Email us
                            </span>
                            <span className="text-base">{CONTACT_EMAIL}</span>
                        </span>
                    </a>
                </Reveal>

                <Reveal delay={0.12}>
                    <div className="bg-cream border border-linec rounded-sm p-8 lg:p-10">
                        <ConsultationForm variant="light" />
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
